import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Mail, Send, ExternalLink, Download, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { emailTemplates } from "@/emails";
import { apiService } from '../../services/ApiService';

const EmailSignature = () => (
  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-sauna-orange">
    <div className="text-sm">
      <p className="mb-2">Warmly,</p>
      <p className="font-medium text-sauna-navy">Michael O'Callaghan</p>
      <p className="text-gray-600">Founder, SaunaDip</p>
      <p className="mt-3 text-gray-700">Now offering the SaunaDip Kit – live sauna + plunge temp tracking, built for wild wellness.</p>
      
      <div className="mt-4 space-y-1">
        <p className="text-sauna-blue hover:underline cursor-pointer">saunadip.com</p>
        <p className="text-sauna-blue hover:underline cursor-pointer">@saunadip</p>
      </div>
      
      <div className="mt-3 text-sm">
        <span>🔥 Find your heat. </span>
        <span>❄️ Brave the cold. </span>
        <span>🌿 Your ritual starts here.</span>
      </div>
    </div>
  </div>
);

const EmailTemplatesShowcase: React.FC = () => {
  const { toast } = useToast();
  const [activeTemplate, setActiveTemplate] = useState<string>('welcome-community-v1');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "Email content has been copied successfully.",
    });
  };

  const openEmailPreview = (templateId: string) => {
    window.open(`${(import.meta as any).env.VITE_EMAIL_PREVIEW_URL || ''}/templates/${templateId}`, '_blank');
  };

  const exportTemplate = async (templateId: string) => {
    try {
      const response = await apiService.get(`export/${templateId}`, { isPublic: true });
      const html = response;
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateId}.html`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Template exported",
        description: `${templateId}.html has been downloaded.`,
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Make sure the React Email dev server is running on port 3000.",
        variant: "destructive",
      });
    }
  };

  const templateCategories = {
    'User Onboarding': ['welcome-community-v1', 'welcome-operator-v1'],
    'Account Management': ['password-reset-v1'],
  };

  const categories = Object.keys(templateCategories);

  return (
    <div className="space-y-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Email Signature */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Signature
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EmailSignature />
              <Button 
                onClick={() => copyToClipboard(`Warmly,
Michael O'Callaghan
Founder, SaunaDip

Now offering the SaunaDip Kit – live sauna + plunge temp tracking, built for wild wellness.

saunadip.com | @saunadip

🔥 Find your heat. ❄️ Brave the cold. 🌿 Your ritual starts here.`)}
                className="w-full mt-4"
                variant="outline"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Signature
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Email Templates */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                React Email Templates
                <Badge variant="secondary" className="ml-2">Live Preview</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={categories[0]} className="w-full">
                <TabsList className="grid w-full grid-cols-2 lg:grid-cols-2 mb-6">
                  {categories.map((category) => (
                    <TabsTrigger 
                      key={category} 
                      value={category}
                      className="text-xs"
                    >
                      {category}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {categories.map((category) => (
                  <TabsContent key={category} value={category} className="space-y-6">
                    {templateCategories[category].map((templateId) => {
                      const template = emailTemplates[templateId];
                      return (
                        <div key={templateId} className="border rounded-lg p-6 space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-medium text-lg">{template.subject}</h3>
                                <Badge variant="outline" className="text-xs">
                                  {templateId}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500 mb-2">{template.description}</p>
                              <div className="flex flex-wrap gap-1">
                                {template.variables.map((variable) => (
                                  <Badge key={variable} variant="secondary" className="text-xs">
                                    {variable}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                onClick={() => openEmailPreview(templateId)}
                                variant="outline"
                                size="sm"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Preview
                              </Button>
                              <Button 
                                onClick={() => exportTemplate(templateId)}
                                variant="outline"
                                size="sm"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Export
                              </Button>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Template ID:</strong> {templateId}
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Subject:</strong> {template.subject}
                            </div>
                            <div className="text-sm text-gray-600">
                              <strong>Variables:</strong> {template.variables.join(', ')}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              onClick={() => copyToClipboard(templateId)}
                              variant="outline"
                              size="sm"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Template ID
                            </Button>
                            <Button 
                              onClick={() => copyToClipboard(template.subject)}
                              variant="outline"
                              size="sm"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Subject
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Development Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Email Development Workflow</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Local Development</h4>
              <div className="space-y-2 text-sm">
                <p><code className="bg-gray-100 px-2 py-1 rounded">npm run emails:dev</code> - Start email preview server</p>
                <p><code className="bg-gray-100 px-2 py-1 rounded">npm run emails:export</code> - Export all templates to HTML</p>
                <p>Visit <a href={(import.meta as any).env.VITE_EMAIL_PREVIEW_URL || ''} target="_blank" className="text-blue-600 hover:underline">{(import.meta as any).env.VITE_EMAIL_PREVIEW_URL || ''}</a> to preview templates</p>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Mailgun Integration</h4>
              <div className="space-y-2 text-sm">
                <p>1. Export template HTML using the Export button</p>
                <p>2. Upload to Mailgun with exact template alias</p>
                <p>3. Backend triggers emails via Mailgun API</p>
                <p>4. Supports both Mailgun API and SMTP</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTemplatesShowcase; 